import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EntriesContext = createContext(null);

export function EntriesProvider({ children }) {
  const { user, activeParticipant } = useAuth();
  const [entries, setEntries] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKeys, setSavingKeys] = useState({});
  const timers = useRef({});

  // The participant whose data we are reading/writing.
  const participantId = user?.role === "coach" ? activeParticipant?.id : null;

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = participantId ? { participant_id: participantId } : {};
      const { data } = await api.get("/entries", { params });
      setEntries(data || {});
    } catch (e) {
      console.error("Failed to load entries:", e);
      setEntries({});
    } finally {
      setLoading(false);
    }
  }, [user, participantId]);

  useEffect(() => { load(); }, [load]);

  const getEntry = useCallback((key, fallback = "") => {
    return entries[key] !== undefined ? entries[key] : fallback;
  }, [entries]);

  const persist = useCallback(async (key, value) => {
    setSavingKeys((s) => ({ ...s, [key]: true }));
    try {
      const params = participantId ? { participant_id: participantId } : {};
      await api.put(`/entries/${encodeURIComponent(key)}`, { value }, { params });
    } catch (e) {
      console.error(`Failed to save entry "${key}":`, e);
    } finally {
      setSavingKeys((s) => ({ ...s, [key]: false }));
    }
  }, [participantId]);

  // optimistic + debounced save
  const saveEntry = useCallback((key, value, immediate = false) => {
    setEntries((e) => ({ ...e, [key]: value }));
    if (timers.current[key]) clearTimeout(timers.current[key]);
    if (immediate) { persist(key, value); return; }
    setSavingKeys((s) => ({ ...s, [key]: true }));
    timers.current[key] = setTimeout(() => persist(key, value), 700);
  }, [persist]);

  const value = useMemo(
    () => ({ entries, loading, getEntry, saveEntry, savingKeys, reload: load }),
    [entries, loading, getEntry, saveEntry, savingKeys, load]
  );

  return (
    <EntriesContext.Provider value={value}>
      {children}
    </EntriesContext.Provider>
  );
}

export const useEntries = () => useContext(EntriesContext);
